/**
 * Storage Cleanup Utility
 * Cleans up corrupted localStorage/sessionStorage to fix auth issues
 *
 * Called automatically on:
 * - Auth failures with corrupted session data
 * - Session restoration failures
 * - Manual cleanup via clearAllAuthStorage()
 */

const HOVER_KEYS = [
  'hover_session_hint',
  'hover_ai_consent',
  'hover_user_preferences',
  'hover_journey_progress',
];

const SUPABASE_KEY_PREFIX = 'sb-';

// supabase-js keys are `sb-<project-ref>-auth-token[-code-verifier]`
const SUPABASE_REF_KEY = /^sb-([a-z0-9]+)-/;

/**
 * Supabase project ref from a project URL (`https://<ref>.supabase.co` → `<ref>`).
 */
export function projectRefFromUrl(supabaseUrl: string): string | null {
  try {
    const host = new URL(supabaseUrl).hostname;
    return host.endsWith('.supabase.co') ? host.split('.')[0] : null;
  } catch {
    return null;
  }
}

/**
 * Remove auth keys left by a different Supabase project.
 *
 * Hover moved from its own project to the shared one (2026). Browsers that signed in
 * before the move can still hold `sb-<old-ref>-…` keys, and with them present sign-in
 * stalled until the code exchange timed out. Keys for the current project — including
 * the PKCE code verifier an OAuth callback needs — are never touched, so this is safe on
 * every page, `/auth/callback` included. Returns the removed keys.
 */
export function removeForeignSupabaseKeys(
  supabaseUrl: string,
  storage: Storage = localStorage,
): string[] {
  const currentRef = projectRefFromUrl(supabaseUrl);
  if (!currentRef) return [];

  const foreign: string[] = [];
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i);
    const ref = key?.match(SUPABASE_REF_KEY)?.[1];
    if (key && ref && ref !== currentRef) foreign.push(key);
  }
  foreign.forEach(key => storage.removeItem(key));
  return foreign;
}

/**
 * Clear all Hover-specific localStorage keys
 */
export function clearHoverStorage(): void {
  console.log('[StorageCleanup] 🧹 Clearing Hover-specific keys...');

  let clearedCount = 0;
  HOVER_KEYS.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
      clearedCount++;
      console.log(`[StorageCleanup] ✓ Removed: ${key}`);
    }
  });

  console.log(`[StorageCleanup] Cleared ${clearedCount} Hover keys`);
}

/**
 * Clear all Supabase auth storage
 * (Supabase stores auth data with 'sb-' prefix)
 */
export function clearSupabaseStorage(): void {
  console.log('[StorageCleanup] 🧹 Clearing Supabase auth keys...');

  const keysToRemove: string[] = [];

  // Find all Supabase keys
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(SUPABASE_KEY_PREFIX)) {
      keysToRemove.push(key);
    }
  }

  // Remove them
  keysToRemove.forEach(key => {
    localStorage.removeItem(key);
    console.log(`[StorageCleanup] ✓ Removed: ${key}`);
  });

  console.log(`[StorageCleanup] Cleared ${keysToRemove.length} Supabase keys`);
}

/**
 * Clear ALL auth-related storage (nuclear option)
 * Use this when localStorage corruption is detected
 */
export function clearAllAuthStorage(): void {
  console.log('[StorageCleanup] 🧹🧹🧹 FULL AUTH CLEANUP - Clearing all storage...');

  clearHoverStorage();
  clearSupabaseStorage();

  // Also clear sessionStorage
  try {
    sessionStorage.clear();
    console.log('[StorageCleanup] ✓ Cleared sessionStorage');
  } catch (error) {
    console.error('[StorageCleanup] Failed to clear sessionStorage:', error);
  }

  console.log('[StorageCleanup] ✅ Full cleanup complete');
}

/**
 * Detect if localStorage appears corrupted
 * Returns true if cleanup is recommended
 */
export function isStorageCorrupted(): boolean {
  try {
    // CRITICAL: Don't clean during OAuth callback or password reset - PKCE keys are expected!
    // During OAuth/recovery flow, we have the code_verifier but no session yet (that's normal)
    const url = new URL(window.location.href);
    const isOAuthCallback = url.pathname.includes('/auth/callback') &&
                           (url.searchParams.has('code') || url.hash.includes('access_token'));
    const isRecoveryFlow = url.hash.includes('type=recovery') || url.searchParams.has('type');

    if (isOAuthCallback || isRecoveryFlow) {
      console.log('[StorageCleanup] ⏭️ Skipping corruption check - Auth flow in progress (OAuth or recovery)');
      return false;
    }

    // Check for malformed session hint
    const hintJson = localStorage.getItem('hover_session_hint');
    if (hintJson) {
      try {
        const hint = JSON.parse(hintJson);
        if (!hint.user_id || !hint.expires_at) {
          console.warn('[StorageCleanup] ⚠️ Malformed session hint detected');
          return true;
        }

        // Check if session hint is expired
        const expiresAt = new Date(hint.expires_at);
        if (expiresAt <= new Date()) {
          console.warn('[StorageCleanup] ⚠️ Expired session hint detected');
          return true;
        }
      } catch {
        console.warn('[StorageCleanup] ⚠️ Unparseable session hint detected');
        return true;
      }
    }

    // Check for orphaned Supabase keys (session exists but no hint)
    const supabaseKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(SUPABASE_KEY_PREFIX)) {
        supabaseKeys.push(key);
      }
    }

    // TEMPORARILY DISABLED: This was too aggressive and logging users out
    // The session hint check needs to be more reliable before we can use this
    // if (supabaseKeys.length > 0 && !hintJson) {
    //   console.warn('[StorageCleanup] ⚠️ Orphaned Supabase keys detected:', supabaseKeys);
    //   return true;
    // }

    // NEW: Check for conflicting auth states (Beta + Supabase)
    const hasBetaAuth = localStorage.getItem('beta_authenticated') === 'true';
    if (supabaseKeys.length > 0 && hasBetaAuth) {
      console.warn('[StorageCleanup] ⚠️ Conflicting Beta + Supabase auth detected');
      return true;
    }

    return false;
  } catch (error) {
    console.error('[StorageCleanup] Error checking storage:', error);
    return true; // If we can't check, assume corruption
  }
}

/**
 * Smart cleanup - only clears if corruption detected
 * Returns true if cleanup was performed
 */
export function cleanupIfCorrupted(): boolean {
  if (isStorageCorrupted()) {
    console.log('[StorageCleanup] 🔧 Corruption detected, running automatic cleanup...');

    // Log what we're cleaning before we clear it
    const supabaseKeys = Object.keys(localStorage).filter(k => k.startsWith('sb-'));
    const hintExists = !!localStorage.getItem('hover_session_hint');

    console.log('[StorageCleanup] Current state:');
    console.log(`  - Supabase keys: ${supabaseKeys.length} (${supabaseKeys.join(', ')})`);
    console.log(`  - Session hint: ${hintExists ? 'exists' : 'missing'}`);

    clearAllAuthStorage();

    console.log('[StorageCleanup] ✅ Cleanup complete - please log in again');
    console.log('[StorageCleanup] If you continue to see this message, click "Clear Login Data & Reset" on the login page');

    return true;
  }

  console.log('[StorageCleanup] ✅ Storage appears healthy - no cleanup needed');
  return false;
}
